export type ExposureLevel = 'low' | 'moderate' | 'high' | 'critical'

export type DimensionId =
    | 'd1-violations'
    | 'd2-scale'
    | 'd3-duration'
    | 'd4-documentation'
    | 'd5-history'

export interface QuestionOption {
    label:string;
    score:number;
}

export interface QuestionDef {
    id:string;
    dimension:DimensionId;
    text:string;
    options:QuestionOption[];
}

export interface DimensionDef {
    id:DimensionId;
    label:string;
    weight:number;  // 0-100, sum across dimensions = 100
    questionIds:string[];
}

export const QUESTIONS:QuestionDef[] = [
    // Dimension 1: Violation Categories (weight 30)
    {
        id: 'q1',
        dimension: 'd1-violations',
        text: 'Are you aware of refrigerant leak rates exceeding EPA thresholds without timely repair?',
        options: [
            { label: 'No known exceedances', score: 0 },
            { label: 'Unsure / not actively monitored', score: 1 },
            { label: 'Occasional exceedances', score: 2 },
            { label: 'Regular exceedances or known unrepaired leaks', score: 3 },
        ],
    },
    {
        id: 'q2',
        dimension: 'd1-violations',
        text: 'Do technicians performing refrigerant work hold valid EPA Section 608 certification?',
        options: [
            { label: 'All technicians are certified', score: 0 },
            { label: 'Occasionally use uncertified technicians', score: 1 },
            { label: 'Frequently use uncertified technicians', score: 2 },
            { label: 'Routinely uncertified or certification status unknown', score: 3 },
        ],
    },
    {
        id: 'q3',
        dimension: 'd1-violations',
        text: 'Are refrigerants ever vented or released rather than recovered during service?',
        options: [
            { label: 'Never', score: 0 },
            { label: 'Rarely / unintentional', score: 1 },
            { label: 'Occasionally', score: 2 },
            { label: 'Regularly or policy not enforced', score: 3 },
        ],
    },
    // Dimension 2: Operational Scope/Scale (weight 20)
    {
        id: 'q4',
        dimension: 'd2-scale',
        text: 'How many refrigerant-containing appliances does your organization manage?',
        options: [
            { label: 'Fewer than 5', score: 0 },
            { label: '5 to 20', score: 1 },
            { label: '21 to 100', score: 2 },
            { label: 'More than 100', score: 3 },
        ],
    },
    {
        id: 'q5',
        dimension: 'd2-scale',
        text: 'What is the maximum refrigerant charge of any single appliance you service?',
        options: [
            { label: 'Less than 50 lbs', score: 0 },
            { label: '50 to 200 lbs', score: 1 },
            { label: '201 to 500 lbs', score: 2 },
            { label: 'More than 500 lbs', score: 3 },
        ],
    },
    // Dimension 3: Duration of Known Gaps (weight 25)
    {
        id: 'q6',
        dimension: 'd3-duration',
        text: 'How long have recordkeeping or compliance gaps existed?',
        options: [
            { label: 'No known gaps', score: 0 },
            { label: 'Less than 6 months', score: 1 },
            { label: '6 months to 2 years', score: 2 },
            { label: 'More than 2 years or duration unknown', score: 3 },
        ],
    },
    {
        id: 'q7',
        dimension: 'd3-duration',
        text: 'How long since your organization last conducted an internal compliance review?',
        options: [
            { label: 'Within the past 6 months', score: 0 },
            { label: '6 to 12 months ago', score: 1 },
            { label: '1 to 3 years ago', score: 2 },
            { label: 'More than 3 years ago or never', score: 3 },
        ],
    },
    // Dimension 4: Documentation Quality (weight 15)
    {
        id: 'q8',
        dimension: 'd4-documentation',
        text: 'How complete are your refrigerant service records?',
        options: [
            { label: 'Complete and current for all appliances', score: 0 },
            { label: 'Minor gaps or some missing entries', score: 1 },
            { label: 'Significant gaps or no consistent format', score: 2 },
            { label: 'No formal recordkeeping system', score: 3 },
        ],
    },
    {
        id: 'q9',
        dimension: 'd4-documentation',
        text: 'How complete are your refrigerant purchase and recovery records?',
        options: [
            { label: 'Full audit trail from supplier to recovery cylinder', score: 0 },
            { label: 'Mostly tracked with minor missing data', score: 1 },
            { label: 'Partially tracked', score: 2 },
            { label: 'Not tracked', score: 3 },
        ],
    },
    // Dimension 5: Historical Compliance Indicators (weight 10)
    {
        id: 'q10',
        dimension: 'd5-history',
        text: 'What is your organization\'s EPA inspection and enforcement history?',
        options: [
            { label: 'Never inspected or no findings', score: 0 },
            { label: 'Inspected with minor findings (corrected)', score: 1 },
            { label: 'Significant findings or Notice of Violation received', score: 2 },
            { label: 'Civil penalties, ongoing enforcement, or repeat violations', score: 3 },
        ],
    },
]

export const DIMENSIONS:DimensionDef[] = [
    {
        id: 'd1-violations',
        label: 'Violation Categories',
        weight: 30,
        questionIds: ['q1', 'q2', 'q3'],
    },
    {
        id: 'd2-scale',
        label: 'Operational Scope / Scale',
        weight: 20,
        questionIds: ['q4', 'q5'],
    },
    {
        id: 'd3-duration',
        label: 'Duration of Known Gaps',
        weight: 25,
        questionIds: ['q6', 'q7'],
    },
    {
        id: 'd4-documentation',
        label: 'Documentation Quality',
        weight: 15,
        questionIds: ['q8', 'q9'],
    },
    {
        id: 'd5-history',
        label: 'Historical Compliance Indicators',
        weight: 10,
        questionIds: ['q10'],
    },
]

export interface DimensionScore {
    id:DimensionId;
    label:string;
    score:number;   // 0-100
    weight:number;
    weightedScore:number;  // score * weight / 100
}

export interface Section608RiskResult {
    totalScore:number;  // 0-100
    exposureLevel:ExposureLevel;
    dimensionScores:DimensionScore[];
    recommendations:string[];
}

function scoreToLevel (score:number):ExposureLevel {
    if (score >= 75) return 'critical'
    if (score >= 50) return 'high'
    if (score >= 25) return 'moderate'
    return 'low'
}

function buildRecommendations (
    level:ExposureLevel,
    answers:Record<string, number>
):string[] {
    const recs:string[] = []

    if (level === 'low') {
        recs.push(
            'Maintain current recordkeeping practices and schedule a ' +
            'compliance self-review at least annually.'
        )
        return recs
    }

    if (answers.q1 !== undefined && answers.q1 >= 2) {
        recs.push(
            'Prioritize repair of all leaking equipment. EPA thresholds ' +
            'require repair within 30 days (or 120 days with an extension).'
        )
    }

    if (answers.q2 !== undefined && answers.q2 >= 1) {
        recs.push(
            'Verify that all technicians handling refrigerants hold current ' +
            'EPA Section 608 certification.'
        )
    }

    if (answers.q3 !== undefined && answers.q3 >= 1) {
        recs.push(
            'Establish and enforce a refrigerant recovery policy for every ' +
            'service event regardless of charge size.'
        )
    }

    if (answers.q6 !== undefined && answers.q6 >= 2) {
        recs.push(
            'Conduct an immediate records audit to reconstruct missing ' +
            'service and refrigerant transaction history.'
        )
    }

    if (answers.q8 !== undefined && answers.q8 >= 2) {
        recs.push(
            'Implement a consistent service-record format. Consider using ' +
            'the Refrigerant Log Generator tool to create compliant records.'
        )
    }

    if (answers.q9 !== undefined && answers.q9 >= 2) {
        recs.push(
            'Establish a complete purchase-to-recovery audit trail for all ' +
            'refrigerant inventory.'
        )
    }

    if (level === 'high' || level === 'critical') {
        recs.push(
            'Consider engaging a qualified environmental compliance consultant ' +
            'before an EPA inspection occurs.'
        )
        recs.push(
            'Review 40 CFR Part 82 Subpart F in detail and use the EPA ' +
            'Audit Checklist tool to assess readiness.'
        )
    } else {
        recs.push(
            'Schedule an internal compliance self-review within the next ' +
            '3 months to address identified gaps.'
        )
    }

    return recs
}

export function calcSection608Risk (
    answers:Record<string, number>
):Section608RiskResult {
    const dimensionScores:DimensionScore[] = DIMENSIONS.map(dim => {
        const questionScores = dim.questionIds.map(
            qId => answers[qId] ?? 0
        )
        const maxPossible = dim.questionIds.length * 3
        const rawSum = questionScores.reduce((s, v) => s + v, 0)
        const score = maxPossible > 0 ?
            (rawSum / maxPossible) * 100 :
            0
        const weightedScore = score * dim.weight / 100

        return {
            id: dim.id,
            label: dim.label,
            score,
            weight: dim.weight,
            weightedScore,
        }
    })

    const totalScore = dimensionScores.reduce(
        (s, d) => s + d.weightedScore, 0
    )
    const exposureLevel = scoreToLevel(totalScore)
    const recommendations = buildRecommendations(exposureLevel, answers)

    return {
        totalScore,
        exposureLevel,
        dimensionScores,
        recommendations,
    }
}
